import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import Button, { ButtonVariants } from '@gym-atoms/Button/Button';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Text from '@gym-atoms/Text/Text';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import { deleteKisiDoor, fetchKisiDoors } from '@gym-redux/slices/kisiDoorSlice';
import { getGymById } from '@gym-redux/slices/gymsSlice';
import type { KisiDoors } from '@gym-particles/types/KisiDoors';
import styles from '@gym-pages/DoorUnlockEventsDashboardPage/EmployeeDashboard.module.scss';

const EmployeeDashboardPage = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { gymId, chainId } = useParams<{ gymId: string; chainId: string }>();
  const { t } = useTranslation();
  const numericGymId = Number(gymId);
  const numericChainId = Number(chainId);

  const gym = useAppSelector((state) =>
    state.gyms.items
      .find((gymChain) => gymChain.chainId === numericChainId)
      ?.items.find((gymItem) => gymItem.gymId === numericGymId)
  );
  const doorGroup = useAppSelector((state) =>
    state.kisiDoor.items.find((group) => group.siteLocationId === numericGymId)
  );
  const doors = doorGroup?.items || [];
  const configuredDoorCount = doorGroup?.totalRecords || doors.length;
  const isDoorsLoading = useAppSelector((state) => state.kisiDoor.isKisiDoorLoading);
  const userId = useAppSelector((state) => state.user.userId);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedDoor, setSelectedDoor] = useState<KisiDoors>();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    dispatch(getGymById({ gymId: numericGymId, gymChainId: numericChainId }));
    dispatch(fetchKisiDoors({ siteLocationId: numericGymId }));
  }, [dispatch, numericChainId, numericGymId]);

  useEffect(() => setLogoFailed(false), [gym?.imageUrl]);

  const closeDeleteDialog = () => {
    setDialogVisible(false);
    setSelectedDoor(undefined);
  };

  const openDeleteDialog = (door: KisiDoors) => {
    setSelectedDoor(door);
    setDialogVisible(true);
  };

  const deleteDialogHandler = async () => {
    if (!selectedDoor) return;
    setIsDeleting(true);
    await dispatch(deleteKisiDoor({ id: selectedDoor.id, lastModifiedBy: userId }));
    setIsDeleting(false);
    closeDeleteDialog();
  };

  const DeleteDialogFooter = () => (
    <div>
      <Button
        data-cy="doors-dialog-cancel-btn"
        label={t('EMPLOYEE_DASHBOARD_PAGE.DELETE_MODAL_CANCEL')}
        variant={ButtonVariants.textonly}
        onClick={closeDeleteDialog}
      />
      <Button
        data-cy="doors-dialog-delete-btn"
        label={t('EMPLOYEE_DASHBOARD_PAGE.DELETE_MODAL_DELETE')}
        variant={ButtonVariants.danger}
        onClick={deleteDialogHandler}
        icon={isDeleting ? 'pi-spinner pi-spin' : ''}
      />
    </div>
  );

  return (
    <div className={`${styles.pageShell} ${styles.doorSelectionPage}`}>
      <div className={styles.dashboard}>
        <DialogBox
          dialogVisible={dialogVisible}
          variant={DialogBoxVariants.long}
          dialogDismissableMask={true}
          onHideCallback={closeDeleteDialog}
          dialogFooter={<DeleteDialogFooter />}
          dialogClosable={false}
          dialogHeader={t('EMPLOYEE_DASHBOARD_PAGE.DELETE_MODAL_HEADER')}
          content={
            <Text data-cy="doors-dialog-content">
              {t('EMPLOYEE_DASHBOARD_PAGE.DELETE_MODAL_CONFIRM')}
              {selectedDoor ? `: ${selectedDoor.kisiDoorName}` : ''}
            </Text>
          }
        />

        <header className={styles.gymHeader}>
          <div className={styles.brandLockup}>
            <button
              type="button"
              className={styles.headerBackButton}
              onClick={() => history.push(`/gymChains/${chainId}/gyms/${gymId}/dashboard`)}>
              <span className="pi pi-arrow-left" aria-hidden="true" />
              {t('EMPLOYEE_DASHBOARD_PAGE.BACK_TO_GYM')}
            </button>
            <div className={styles.logoPlate}>
              {gym?.imageUrl && !logoFailed ? (
                <img
                  src={gym.imageUrl}
                  className={styles.gymLogo}
                  alt={`${gym.gymName} logo`}
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <div className={styles.fallbackLogo} aria-hidden="true">
                  {(gym?.gymName || 'GX').slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
          </div>
          <div className={styles.headerTitle}>
            <p className={styles.gymName}>
              {gym?.gymName || t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.GYM_FALLBACK')}
            </p>
            <p className={styles.doorName}>{t('EMPLOYEE_DASHBOARD_PAGE.LIVE_ACCESS')}</p>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() =>
                history.push(`/gymChains/${chainId}/gyms/${gymId}/liveAccess/doors/new`)
              }>
              <span className="pi pi-plus" aria-hidden="true" />
              {t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.BUTTON_ADD_LABEL')}
            </button>
          </div>
        </header>

        <main className={styles.doorSelectionMain}>
          <section className={`${styles.card} ${styles.doorSelectionPanel}`}>
            <header className={styles.doorSelectionHeading}>
              <div>
                <div className={styles.cardTitle}>
                  <span className="pi pi-sign-in" aria-hidden="true" />
                  <h1>{t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.KISI_DOOR_TABLE_HEADER')}</h1>
                </div>
                <p>{t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.SELECT_DOOR_DESCRIPTION')}</p>
              </div>
              <span className={styles.doorCount}>
                {t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.CONFIGURED_DOORS', {
                  count: configuredDoorCount
                })}
              </span>
            </header>

            {isDoorsLoading ? (
              <div className={styles.emptyState}>
                <span className="pi pi-spin pi-spinner" aria-hidden="true" />
                <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LOADING')}</span>
              </div>
            ) : doors.length > 0 ? (
              <div className={styles.doorGrid}>
                {doors.map((door) => (
                  <article key={door.id} className={styles.doorCard}>
                    <button
                      type="button"
                      className={styles.doorLaunchButton}
                      onClick={() =>
                        history.push(
                          `/gymChains/${chainId}/gyms/${gymId}/liveAccess/doors/${door.id}/events`
                        )
                      }>
                      <span className={styles.doorIcon} aria-hidden="true">
                        <span className="pi pi-sign-in" />
                      </span>
                      <span className={styles.doorIdentity}>
                        <strong>{door.kisiDoorName}</strong>
                        <span>{door.kisiPlaceName}</span>
                      </span>
                      <span className={styles.openLiveView}>
                        {t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.OPEN_LIVE_VIEW')}
                        <span className="pi pi-arrow-right" aria-hidden="true" />
                      </span>
                    </button>

                    <dl className={styles.doorMetadata}>
                      <div>
                        <dt>{t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.KISI_DOOR_ID_HEADER')}</dt>
                        <dd>{door.kisiDoorId}</dd>
                      </div>
                      <div>
                        <dt>
                          {t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.KISI_PLACE_ID_HEADER')}
                        </dt>
                        <dd>{door.kisiPlaceId}</dd>
                      </div>
                      <div>
                        <dt>
                          {t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.LAST_MODIFIED_DATE_HEADER')}
                        </dt>
                        <dd>{door.lastModifiedDate || '—'}</dd>
                      </div>
                    </dl>

                    <footer className={styles.doorCardFooter}>
                      <button
                        type="button"
                        className={styles.dangerTextButton}
                        onClick={() => openDeleteDialog(door)}>
                        <span className="pi pi-trash" aria-hidden="true" />
                        {t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.BUTTON_DELETE_LABEL')}
                      </button>
                    </footer>
                  </article>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <span className="pi pi-sign-in" aria-hidden="true" />
                <span>{t('EMPLOYEE_DASHBOARD_PAGE.KISI_DOORS_TABLE.EMPTY_STATE_TEXT')}</span>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default EmployeeDashboardPage;
